<template>
  <div
    :class="{
      [$style.leftNavLinkContainer]: true,
      [$style.selected]: selected,
    }"
  >
    <NuxtLink
      :class="$style.leftNavLink"
      :to="link.to"
      :aria-current="selected ? 'page' : undefined"
    >
      <component
        :is="iconMap[link.icon]"
        :class="$style.leftNavLinkLogo"
        aria-hidden="true"
      />
      <span :class="[$style.linkLabel, 'type-label-large-emphasized']">
        {{ link.label }}
      </span>
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
  import type { IconName } from '~/constants/iconTypes'
  import { iconMap } from '~/constants/iconTypes'

  interface LeftNavLink {
    label: string
    to: string
    icon: IconName
  }

  interface LeftNavLinkProps {
    selected?: boolean
    link: LeftNavLink
  }

  withDefaults(defineProps<LeftNavLinkProps>(), {
    selected: false,
  })
</script>

<style module>
  .leftNavLinkContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(var(--space-32) * 2);
    min-height: calc(var(--space-32) * 2);
    width: 100%;
    border-radius: var(--radius-md);
    background-color: transparent;
    overflow: hidden;
    transition:
      background-color 160ms ease,
      transform 160ms ease;
  }

  .leftNavLinkContainer:hover:not(.selected) {
    background-color: var(--color-interactive-tertiary-hover);
  }

  .leftNavLink {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    text-decoration: none;
    width: 100%;
    height: 100%;
    padding: var(--space-4) var(--space-2);
    border: none;
    background: transparent;
    cursor: pointer;
  }

  .leftNavLink:active {
    transform: scale(0.97);
  }

  .leftNavLink:focus-visible {
    outline: 2px solid var(--color-interactive-primary);
    outline-offset: -2px;
  }

  .leftNavLinkLogo {
    height: var(--space-24);
    width: var(--space-24);
    flex-shrink: 0;
    color: var(--color-icon-primary);
    fill: currentColor;
  }

  .linkLabel {
    color: var(--color-text-primary);
    white-space: nowrap;
    text-align: center;
  }

  .selected {
    background-color: var(--color-interactive-primary);
  }

  .selected .leftNavLinkLogo {
    color: var(--color-icon-on-brand) !important;
    fill: currentColor !important;
  }

  .selected .linkLabel {
    color: var(--color-text-on-brand);
  }

  .selected .leftNavLink:focus-visible {
    outline-color: var(--color-text-on-brand);
  }

  .selected:hover {
    background-color: var(--color-interactive-primary-selected);
    cursor: default;
  }
</style>
